import { Reveal } from "./Reveal";

const OUTCOMES = [
    "Conversation",
    "Friendship",
    "Dating",
    "Creative connection",
    "Something unexpected",
];

const OutcomesNote = () => (
    <div data-testid="principle-01-outcomes" className="frame rounded-sm p-6 sm:p-8">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#8A8580]">
            One encounter → many possible outcomes
        </p>
        <ul className="mt-5">
            {OUTCOMES.map((o) => (
                <li
                    key={o}
                    className="flex items-center justify-between border-t border-[#262422] py-3 first:border-t-0 first:pt-0 last:pb-0"
                >
                    <span className="text-sm text-[#F4F1EC] sm:text-base">{o}</span>
                    <span className="text-[#C7B7FF]" aria-hidden="true">
                        →
                    </span>
                </li>
            ))}
        </ul>
    </div>
);

const REP_TILES = [
    {
        label: "Avatar",
        icon: (
            <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" stroke="#C9C5C0" strokeWidth="1.5">
                <circle cx="16" cy="12" r="6" />
                <path d="M5 27c2-6 6-8 11-8s9 2 11 8" />
            </svg>
        ),
    },
    {
        label: "Illustration",
        icon: (
            <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" stroke="#C9C5C0" strokeWidth="1.5">
                <path d="M4 24l8-12 5 7 4-5 7 10z" />
                <circle cx="23" cy="8" r="3" />
            </svg>
        ),
    },
    {
        label: "Personal photo",
        icon: (
            <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" stroke="#C9C5C0" strokeWidth="1.5">
                <rect x="4" y="7" width="24" height="18" rx="2" />
                <circle cx="16" cy="16" r="5" />
            </svg>
        ),
    },
    {
        label: "Artwork",
        icon: (
            <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" stroke="#C9C5C0" strokeWidth="1.5">
                <path d="M6 22c4-10 8 4 12-6 3-7 6 2 8 6" />
                <path d="M6 26h20" strokeDasharray="2 3" />
            </svg>
        ),
    },
];

const RepresentationGrid = () => (
    <div data-testid="principle-02-representations" className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-[#262422] bg-[#262422]">
        {REP_TILES.map((t) => (
            <div key={t.label} className="flex flex-col items-center gap-3 bg-[#141312] px-4 py-7">
                {t.icon}
                <span className="font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#8A8580]">
                    {t.label}
                </span>
            </div>
        ))}
    </div>
);

const MOTIFS = {
    connection: (
        <svg viewBox="0 0 96 48" className="h-10 w-20 shrink-0" fill="none" aria-hidden="true">
            <circle cx="14" cy="34" r="6" stroke="#C9C5C0" strokeWidth="1.5" />
            <circle cx="82" cy="14" r="6" stroke="#C7B7FF" strokeWidth="1.5" />
            <path d="M20 31 C 45 34, 55 12, 76 16" stroke="#8A8580" strokeWidth="1.2" strokeDasharray="3 4" />
        </svg>
    ),
    search: (
        <svg viewBox="0 0 48 48" className="h-10 w-10 shrink-0" fill="none" aria-hidden="true">
            <circle cx="20" cy="20" r="11" stroke="#C9C5C0" strokeWidth="1.5" />
            <path d="M28.5 28.5L40 40" stroke="#C7B7FF" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M15 20h10M20 15v10" stroke="#8A8580" strokeWidth="1.2" />
        </svg>
    ),
    popularity: (
        <span className="relative shrink-0 font-mono2 text-sm tracking-widest text-[#8A8580]" aria-hidden="true">
            12,408
            <span className="absolute left-[-6px] right-[-6px] top-1/2 h-px -translate-y-1/2 -rotate-6 bg-[#C7B7FF]" />
        </span>
    ),
    geography: (
        <svg viewBox="0 0 96 48" className="h-10 w-20 shrink-0" fill="none" aria-hidden="true">
            <circle cx="18" cy="24" r="5" stroke="#C7B7FF" strokeWidth="1.5" />
            <circle cx="42" cy="24" r="3.5" stroke="#C9C5C0" strokeWidth="1.5" />
            <circle cx="80" cy="24" r="3.5" stroke="#C9C5C0" strokeWidth="1.5" />
            <path d="M24 24h13M47 24h28" stroke="#8A8580" strokeWidth="1.2" strokeDasharray="3 4" />
        </svg>
    ),
    equality: (
        <svg viewBox="0 0 48 48" className="h-10 w-10 shrink-0" fill="none" aria-hidden="true">
            <rect x="8" y="12" width="14" height="24" stroke="#C9C5C0" strokeWidth="1.5" />
            <rect x="26" y="12" width="14" height="24" stroke="#C7B7FF" strokeWidth="1.5" />
            <path d="M8 24h32" stroke="#8A8580" strokeWidth="1" strokeDasharray="2 3" />
        </svg>
    ),
};

const Large = ({ testid, num, title, children, visual, flip }) => (
    <div data-testid={testid} className="border-t border-[#262422] py-14 sm:py-20">
        <Reveal>
            <div className={`grid items-center gap-10 lg:grid-cols-12 ${flip ? "" : ""}`}>
                <div className={`lg:col-span-6 ${flip ? "lg:order-2 lg:col-start-7" : ""}`}>
                    <p className="font-mono2 text-xs uppercase tracking-[0.22em] text-[#C7B7FF]">
                        Principle {num}
                    </p>
                    <h3 className="mt-6 max-w-xl text-2xl font-semibold leading-tight tracking-tight text-[#F4F1EC] sm:text-4xl">
                        {title}
                    </h3>
                    <div className="mt-7 max-w-xl space-y-4 text-base leading-relaxed text-[#C9C5C0] sm:text-lg">
                        {children}
                    </div>
                </div>
                <div className={`lg:col-span-4 ${flip ? "lg:order-1 lg:col-start-1" : "lg:col-start-9"}`}>
                    {visual}
                </div>
            </div>
        </Reveal>
    </div>
);

const Compact = ({ testid, num, title, motif, children }) => (
    <div data-testid={testid} className="border-t border-[#262422] py-10 sm:py-12">
        <Reveal>
            <div className="flex items-start justify-between gap-6">
                <div>
                    <p className="font-mono2 text-xs uppercase tracking-[0.22em] text-[#C7B7FF]">
                        Principle {num}
                    </p>
                    <h3 className="mt-4 text-xl font-semibold leading-snug tracking-tight text-[#F4F1EC] sm:text-2xl">
                        {title}
                    </h3>
                    <div className="mt-4 max-w-md space-y-3 text-sm leading-relaxed text-[#C9C5C0] sm:text-base">
                        {children}
                    </div>
                </div>
                <div className="hidden pt-6 sm:block">{MOTIFS[motif]}</div>
            </div>
        </Reveal>
    </div>
);

export const Principles = () => (
    <section id="principles" data-testid="principles-section" className="px-5 py-24 sm:px-8 sm:py-36">
        <div className="mx-auto max-w-[1500px]">
            <Reveal>
                <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#8A8580]">
                    Principles
                </p>
            </Reveal>
            <div className="mt-10">
                <Large
                    testid="principle-not-just-dating"
                    num="01"
                    title="Dating is one possibility. Not the whole point."
                    visual={<OutcomesNote />}
                >
                    <p>
                        Discovery is about finding people you genuinely want to know. That might
                        become dating, friendship, conversation, creative collaboration, or
                        something neither person predicted.
                    </p>
                </Large>
                <Large
                    testid="principle-not-appearance-first"
                    num="02"
                    title="Your face doesn't have to be your profile."
                    visual={<RepresentationGrid />}
                    flip
                >
                    <p>
                        Personal photos should be optional. Avatars, artwork and other
                        identity-signalling images can be meaningful ways to represent yourself.
                    </p>
                </Large>
                <div className="grid gap-x-16 lg:grid-cols-2">
                    <Compact testid="principle-03-people-before-engagement" num="03" title="People before engagement." motif="connection">
                        <p>
                            Success means helping the right people find each other — not keeping
                            them scrolling forever.
                        </p>
                        <p>
                            If someone makes a meaningful connection and eventually needs the
                            platform less, that can still be success.
                        </p>
                    </Compact>
                    <Compact testid="principle-04-discovery-in-your-hands" num="04" title="Discovery should stay in your hands." motif="search">
                        <p>
                            Recommendations can help, but they should not be the only way to find
                            people.
                        </p>
                        <p>
                            Users should be able to deliberately search through meaningful
                            interests, works, topics and other identity signals.
                        </p>
                    </Compact>
                    <Compact testid="principle-05-relevance-not-popularity" num="05" title="Relevance, not popularity." motif="popularity">
                        <p>
                            People should be surfaced because they may matter to you — not because
                            they already have the most followers, likes or attention.
                        </p>
                        <p>Do not turn people into popularity scores.</p>
                    </Compact>
                    <Compact testid="principle-06-geography" num="06" title="Geography matters. Borders shouldn't decide who exists." motif="geography">
                        <p>Nearby compatibility can be especially valuable.</p>
                        <p>
                            But distance should shape relevance rather than arbitrarily making an
                            unusually compatible person disappear.
                        </p>
                    </Compact>
                    <Compact testid="principle-07-no-pay-to-win" num="07" title="Money shouldn't decide who you can meet." motif="equality">
                        <p>Core discovery, search and communication should not become pay-to-win.</p>
                        <p>
                            If monetization exists later, it should add to the experience rather
                            than deliberately damage the free social-discovery system.
                        </p>
                    </Compact>
                </div>
            </div>
        </div>
    </section>
);
